import { createCheckout, verifyCheckout, type BillingPlanCode } from '@/api/billing'
import { loadRazorpay } from '@/lib/razorpay'

interface Prefill {
  name?: string
  email?: string
}

/**
 * Runs the whole Checkout round-trip for a plan: create the subscription on our backend, open
 * the Razorpay modal, and verify the result. Resolves `true` on a verified payment, `false` if
 * the user dismissed the modal. Rejects on any real error.
 */
export function openRazorpayCheckout(
  planCode: BillingPlanCode,
  prefill: Prefill = {}
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const order = await createCheckout(planCode)
        const Razorpay = await loadRazorpay()
        let settled = false

        const rz = new Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency,
          name: 'LogMySpends',
          description: `${planCode.toLowerCase()} plan`,
          subscription_id: order.razorpaySubscriptionId,
          prefill: { name: prefill.name, email: prefill.email },
          theme: { color: readBrandColor() },
          handler: (r) => {
            settled = true
            verifyCheckout({
              razorpaySubscriptionId: r.razorpay_subscription_id,
              razorpayPaymentId: r.razorpay_payment_id,
              razorpaySignature: r.razorpay_signature,
            })
              .then(() => resolve(true))
              .catch(reject)
          },
          modal: {
            ondismiss: () => {
              if (!settled) resolve(false)
            },
          },
        })
        rz.on('payment.failed', (r) => {
          settled = true
          reject(new Error(r.error?.description ?? 'The payment failed. No money was taken.'))
        })
        rz.open()
      } catch (err) {
        reject(err)
      }
    })()
  })
}

function readBrandColor(): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--fl-primary').trim()
    return v || '#2d7a5f'
  } catch {
    return '#2d7a5f'
  }
}
